import React, { useState, useEffect } from 'react';
import {
  CalendarCheck,
  ArrowRight,
  TrendingUp,
  Users,
  FileText,
  Zap,
  ChevronRight,
  Sun,
  Moon,
  CloudSun,
  Receipt,
  BarChart3,
  Wallet,
  Building2,
  Package,
} from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';

const DashboardHome = ({ user, onNavigate }) => {
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  const isAdmin = user?.role === 'Superadmin' || user?.role === 'Admin';

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return { text: 'Good Morning', icon: Sun, color: 'text-amber-500' };
    if (hour < 17) return { text: 'Good Afternoon', icon: CloudSun, color: 'text-orange-500' };
    return { text: 'Good Evening', icon: Moon, color: 'text-indigo-400' };
  };

  const greeting = getGreeting();
  const GreetingIcon = greeting.icon;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString('en-MY', { day: 'numeric', month: 'short' });
  };

  // Fetch dashboard data
  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch overview data
      const endpoint = isAdmin 
        ? `${getApiUrl()}/api/analytics/admin/overview`
        : `${getApiUrl()}/api/analytics/user/overview`;

      const [overviewRes, salesRes] = await Promise.all([
        fetch(endpoint, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/analytics/recent-sales?limit=5`, { headers: getAuthHeaders() }),
      ]);

      const overviewData = await overviewRes.json();
      const salesData = await salesRes.json();

      if (overviewData.success) {
        setDashboardData(overviewData.data);
      }

      if (salesData.success) {
        setRecentSales(salesData.data);
      }

    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Quick actions based on role - different actions for admin vs users
  const quickActions = isAdmin ? [
    { 
      label: 'Record Sale', 
      icon: Receipt, 
      color: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
      action: () => onNavigate('sales-management')
    },
    { 
      label: 'Manage Groups', 
      icon: Building2, 
      color: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700',
      action: () => onNavigate('group-management')
    },
    { 
      label: 'Manage Products', 
      icon: Package, 
      color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
      action: () => onNavigate('product-management')
    },
  ] : [
    { 
      label: 'My Commissions', 
      icon: Wallet, 
      color: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
      action: () => onNavigate('my-commissions')
    },
    { 
      label: 'View Analytics', 
      icon: BarChart3, 
      color: 'bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700',
      action: () => onNavigate('analytics')
    },
    { 
      label: 'My Group', 
      icon: Users, 
      color: 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600',
      action: () => onNavigate('my-group')
    },
  ];

  // Motivational tips
  const tips = [
    "💡 If you are not taking care of your customer, your competitor will.",
    "🎯 Set daily targets to stay focused and motivated",
    "📈 Chase the vision, not the money; the money will end up following you.",
    "🤝 Building relationships leads to long-term success",
    "⭐ Quality over quantity - focus on qualified leads",
  ];
  const randomTip = tips[Math.floor(Math.random() * tips.length)];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-6">
      {/* Welcome Banner - Light gradient design */}
      <div className="relative overflow-hidden bg-gradient-to-r from-primary-500 via-primary-600 to-primary-700 dark:from-primary-600 dark:via-primary-700 dark:to-primary-800 rounded-2xl p-6 lg:p-8 text-white">
        <div className="absolute top-0 right-0 w-72 h-72 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/3"></div>
        <div className="absolute bottom-0 left-0 w-56 h-56 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4"></div>
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-white/10 rounded-full"></div>
        
        <div className="relative z-10">
          <div className="flex items-center gap-2 mb-2">
            <GreetingIcon className={`w-6 h-6 ${greeting.color}`} />
            <span className="text-white/80 text-sm font-medium">{greeting.text}</span>
          </div>
          
          <h1 className="text-2xl lg:text-3xl font-bold mb-2">
            Welcome back, {user?.name?.split(' ')[0] || 'User'}!
          </h1>
          
          <p className="text-white/80 mb-6 max-w-xl">
            {isAdmin 
              ? "Here's what's happening in your organization today."
              : "Here's your performance snapshot for today."}
          </p>

          {/* Quick Stats in Banner */}
          <div className="flex flex-wrap gap-4 lg:gap-6">
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
              <p className="text-xs text-white/70 uppercase tracking-wide font-medium">This Month</p>
              <p className="text-xl lg:text-2xl font-bold">
                {formatCurrency(
                  isAdmin 
                    ? dashboardData?.currentMonth?.sales || 0
                    : dashboardData?.currentMonth?.sales || 0
                )}
              </p>
            </div>
            
            {isAdmin ? (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                <p className="text-xs text-white/70 uppercase tracking-wide font-medium">Active Users</p>
                <p className="text-xl lg:text-2xl font-bold">{dashboardData?.users?.total || 0}</p>
              </div>
            ) : (
              <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
                <p className="text-xs text-white/70 uppercase tracking-wide font-medium">Your Rank</p>
                <p className="text-xl lg:text-2xl font-bold">
                  #{dashboardData?.rank || '-'}
                  <span className="text-sm font-normal text-white/70">
                    {dashboardData?.totalInGroup ? ` of ${dashboardData.totalInGroup}` : ''}
                  </span>
                </p>
              </div>
            )}
            
            <div className="bg-white/20 backdrop-blur-sm rounded-xl px-5 py-3 border border-white/20">
              <p className="text-xs text-white/70 uppercase tracking-wide font-medium">
                {isAdmin ? 'YTD Sales' : 'Commission'}
              </p>
              <p className="text-xl lg:text-2xl font-bold">
                {formatCurrency(
                  isAdmin 
                    ? dashboardData?.ytd?.sales || 0
                    : dashboardData?.currentMonth?.commission || 0
                )}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-800 dark:text-dark-text mb-3">Quick Actions</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {quickActions.map((action, index) => {
            const Icon = action.icon;
            return (
              <button
                key={index}
                onClick={action.action}
                className={`${action.color} text-white rounded-xl p-4 flex items-center justify-between transition-all duration-200 transform hover:scale-[1.02] hover:shadow-lg shadow-md`}
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{action.label}</span>
                </div>
                <ArrowRight className="w-5 h-5" />
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Sales Activity */}
        <div className="lg:col-span-2 bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden transition-colors duration-200">
          <div className="p-4 border-b border-gray-100 dark:border-dark-border flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800 dark:text-dark-text">
                {isAdmin ? 'Recent Sales' : 'Group Recent Sales'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-dark-muted">
                {isAdmin ? 'Latest transactions' : 'Latest transactions from your group'}
              </p>
            </div>
            {/* Only show View All for admin */}
            {isAdmin && (
              <button 
                onClick={() => onNavigate('sales-management')}
                className="text-sm text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium flex items-center gap-1"
              >
                View All <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
          
          <div className="divide-y divide-gray-100 dark:divide-dark-border">
            {recentSales.length > 0 ? (
              recentSales.map((sale) => (
                <div key={sale.id} className="p-4 hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-lg flex items-center justify-center text-white font-bold text-sm">
                        {sale.user?.name?.charAt(0) || 'S'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-dark-text">{sale.user?.name || 'Unknown'}</p>
                        <p className="text-sm text-gray-500 dark:text-dark-muted">{sale.product?.name || 'Product'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-gray-900 dark:text-dark-text">{formatCurrency(sale.amount)}</p>
                      <p className="text-xs text-gray-500 dark:text-dark-muted">{formatDate(sale.date)}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center text-gray-500 dark:text-dark-muted">
                <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-slate-600" />
                <p className="font-medium">No recent sales</p>
                <p className="text-sm text-gray-400 dark:text-dark-muted mt-1">
                  {isAdmin ? 'Start by recording a sale' : 'Sales will appear here'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="space-y-6">
          {/* Today's Date Card */}
          <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-5 text-white">
            <div className="flex items-center gap-2 mb-3">
              <CalendarCheck className="w-5 h-5" />
              <span className="text-sm font-medium text-indigo-100">Today</span>
            </div>
            <p className="text-2xl font-bold">
              {currentTime.toLocaleDateString('en-MY', { 
                weekday: 'long',
                day: 'numeric',
                month: 'long'
              })}
            </p>
            <p className="text-indigo-200 mt-1">
              {currentTime.toLocaleTimeString('en-MY', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              })}
            </p>
          </div>

          {/* Performance Tip */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700/50 rounded-xl p-5 transition-colors duration-200">
            <div className="flex items-start gap-3">
              <div className="bg-amber-100 dark:bg-amber-800/50 p-2 rounded-lg">
                <Zap className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h4 className="font-semibold text-amber-900 dark:text-amber-300 mb-1">Pro Tip</h4>
                <p className="text-sm text-amber-800 dark:text-amber-200">{randomTip}</p>
              </div>
            </div>
          </div>

          {/* Status Overview for Admin */}
          {isAdmin && dashboardData && (
            <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-5 transition-colors duration-200">
              <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-4">Team Overview</h4>
              <div className="space-y-3">
                {dashboardData.users?.byRole && Object.entries(dashboardData.users.byRole).map(([role, count]) => (
                  <div key={role} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 dark:text-dark-muted">{role}</span>
                    <span className="font-semibold text-gray-900 dark:text-dark-text">{count}</span>
                  </div>
                ))}
                <div className="pt-3 border-t border-gray-100 dark:border-dark-border flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700 dark:text-dark-muted">Active Groups</span>
                  <span className="font-bold text-primary-600 dark:text-primary-400">{dashboardData.groups || 0}</span>
                </div>
              </div>
            </div>
          )}

          {/* Growth Indicator for Users */}
          {!isAdmin && dashboardData && (
            <div className="bg-white dark:bg-dark-card rounded-xl shadow-sm border border-gray-200 dark:border-dark-border p-5 transition-colors duration-200">
              <h4 className="font-semibold text-gray-800 dark:text-dark-text mb-4">Your Progress</h4>
              <div className="flex items-center gap-4">
                <div className={`p-3 rounded-xl ${
                  dashboardData.growthPercent >= 0 ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
                }`}>
                  <TrendingUp className={`w-6 h-6 ${
                    dashboardData.growthPercent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`} />
                </div>
                <div>
                  <p className="text-sm text-gray-500 dark:text-dark-muted">vs Last Month</p>
                  <p className={`text-2xl font-bold ${
                    dashboardData.growthPercent >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                  }`}>
                    {dashboardData.growthPercent >= 0 ? '+' : ''}{dashboardData.growthPercent}%
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500 dark:text-dark-muted mt-3">
                {dashboardData.growthPercent >= 0 
                  ? "Great job! Keep up the momentum! 🎉" 
                  : "Let's push harder this month! 💪"}
              </p>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default DashboardHome;
